import type { RGB } from '@tma.js/sdk-react'

/** Цвет с каналами 0–255 и альфой 0–1. */
export type Rgba = { r: number; g: number; b: number; a: number }

const toRgba = (values: number[]): Rgba | null => {
  const [r, g, b, a = 1] = values

  return values.length <= 4 && values.every(Number.isFinite) && r !== undefined && g !== undefined && b !== undefined
    ? { r, g, b, a: Math.min(1, Math.max(0, a)) }
    : null
}

/**
 * Значение CSS-цвета → каналы. Понимает `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`
 * и `rgb()` / `rgba()` через запятую или пробел. Короткие и восьмизначные
 * формы — не прихоть: минификатор сборки пишет `#ffffff` как `#fff`, а
 * `rgba(118, 118, 128, 0.12)` — как `#7676801f`, и значения токенов в проде
 * не совпадают с исходником `app/styles`.
 */
export const parseCssColor = (value: string): Rgba | null => {
  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value)?.[1]

  if (hex) {
    const digits = hex.length <= 4 ? [...hex].map((digit) => digit + digit).join('') : hex
    const channels = [0, 2, 4, 6].filter((at) => at < digits.length).map((at) => parseInt(digits.slice(at, at + 2), 16))

    return toRgba(channels.map((channel, index) => (index === 3 ? channel / 255 : channel)))
  }

  const parts = /^rgba?\(([^)]+)\)$/i
    .exec(value)?.[1]
    ?.split(/[\s,/]+/)
    .filter(Boolean)

  return parts ? toRgba(parts.map((part) => (part.endsWith('%') ? parseFloat(part) / 100 : Number(part)))) : null
}

const toHex = (channel: number): string =>
  Math.round(Math.min(255, Math.max(0, channel)))
    .toString(16)
    .padStart(2, '0')

/** Непрозрачный цвет в виде `#rrggbb` — другого SDK не принимает. */
export const toRgb = ({ r, g, b }: Rgba): RGB => `#${toHex(r)}${toHex(g)}${toHex(b)}` as RGB

/** Полупрозрачный цвет поверх непрозрачного — то, что видит глаз. */
export const blendOver = (top: Rgba, bottom: Rgba): Rgba => {
  const mix = (over: number, under: number): number => over * top.a + under * (1 - top.a)

  return { r: mix(top.r, bottom.r), g: mix(top.g, bottom.g), b: mix(top.b, bottom.b), a: 1 }
}

/** Значение CSS-токена на `<html>` как есть (`var()` уже подставлены браузером). */
export const readCssToken = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

/**
 * CSS-токен цвета на `<html>` → `#rrggbb` для SDK. Непрозрачный цвет в любой
 * записи; полупрозрачный и всё прочее (`color-mix`, имена цветов) — `null`:
 * его нужно смешать с подложкой (`blendOver`), а не обрезать альфу.
 */
export const readColorToken = (name: string): RGB | null => {
  const color = parseCssColor(readCssToken(name))

  return color && color.a === 1 ? toRgb(color) : null
}
